import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { ChatTeleconsultationActions } from 'applicaiton/constants/actions';
import { ChatTeleconsultationAPI } from 'integration/api/chatTeleconsultation';
import { UploadChatTeleconsultationDocumentParams } from 'common/types/chat';

/**
 * Asynchronous thunk to upload a chat teleconsultation document.
 *
 * @param data - The parameters for uploading the document.
 * @returns The response data if the request is successful, or the error message if it fails.
 */
export const uploadChatTeleconsultationDocument = createAsyncThunk(
  ChatTeleconsultationActions.uploadChatTeleconsultationDocument,
  async (
    data: UploadChatTeleconsultationDocumentParams,
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } =
        await ChatTeleconsultationAPI.uploadChatTeleconsultationDocument(data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronous thunk to download a chat teleconsultation document.
 *
 * @param messageSid - The SID of the message containing the document.
 * @returns The response data if the request is successful, or the error message if it fails.
 */
export const downloadChatTeleconsultationDocument = createAsyncThunk(
  ChatTeleconsultationActions.downloadChatTeleconsultationDocument,
  async (messageSid: string, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await ChatTeleconsultationAPI.downloadChatTeleconsultationDocument(
          messageSid,
        );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronous thunk to delete a chat document message.
 *
 * @param messageSid - The SID of the message to be deleted.
 * @returns The response data if the request is successful, or the error message if it fails.
 */
export const deleteChatDocumentMessage = createAsyncThunk(
  ChatTeleconsultationActions.deleteChatDocumentMessage,
  async (messageSid: string, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await ChatTeleconsultationAPI.deleteChatDocumentMessage(messageSid);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
